/**
 * Recovery for stale-deployment dynamic-import failures.
 *
 * Vite code-splits every lazy route (see src/App.tsx) into its own hashed
 * chunk file. When a new deploy replaces those hashes while a user still has
 * an older index.html loaded, the next lazy-route navigation 404s — surfacing
 * as "Failed to fetch dynamically imported module" (Chromium), "error loading
 * dynamically imported module" (Firefox), or "Importing a module script
 * failed" (Safari). The fix is a single reload to pick up the fresh
 * index.html/asset manifest, not a soft in-app retry (which re-runs the exact
 * same stale import and fails identically).
 */

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk .* failed/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_ERROR_PATTERN.test(message);
}

const RELOAD_GUARD_KEY = "orchy:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

/** Reloads the page, but only once per cooldown window — avoids a reload loop if the failure isn't actually stale-chunk-shaped (e.g. a real outage). */
export function reloadForFreshChunks(): void {
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
}
